/**
 * Tee-switch pill: tap to flip the round's tee colour mid-play. Shows the
 * current tee colour as a coloured dot + label. The dropdown lists the
 * course's `tees_available` (falling back to the standard UK set) so the
 * player can switch without restarting the round. Persists to the round
 * doc via `PATCH /rounds/{id}/tee`.
 */
import { useState } from "react";
import { api } from "../../lib/api";

const TEE_OPTIONS = [
  { id: "",       label: "Default", swatch: "transparent" },
  { id: "white",  label: "White",   swatch: "#ffffff" },
  { id: "yellow", label: "Yellow",  swatch: "#FFE45E" },
  { id: "blue",   label: "Blue",    swatch: "#3B82F6" },
  { id: "red",    label: "Red",     swatch: "#ef4444" },
  { id: "black",  label: "Black",   swatch: "#111111" },
  { id: "gold",   label: "Gold",    swatch: "#D4AF37" },
  { id: "green",  label: "Green",   swatch: "#16a34a" },
];

export default function TeeSwitchPill({ round, course, activePlayer = 0, onTeeChanged }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  // Fourball: each player has their own `tee_color` on round.players[i].
  // Solo: fall back to the round-level tee_color (host's chosen tee).
  const isFourball = Array.isArray(round?.players) && round.players.length > 1;
  const player = isFourball ? round?.players?.[activePlayer] : null;
  const current = ((isFourball ? player?.tee_color : round?.tee_color) || "").toLowerCase();
  // Only show colours either currently set OR that the course actually has yardages for.
  const available = new Set(
    (course?.tees_available || []).map((t) => (t || "").toLowerCase())
  );
  const opts = TEE_OPTIONS.filter(
    (o) => o.id === "" || o.id === current || available.has(o.id),
  );
  const showOpts = opts.length > 1 ? opts : TEE_OPTIONS; // fallback to full list
  const currentOpt = TEE_OPTIONS.find((o) => o.id === current) || TEE_OPTIONS[0];

  const pick = async (next) => {
    if (busy || next === current) { setOpen(false); return; }
    setBusy(true);
    try {
      const url = isFourball
        ? `/rounds/${round.id}/players/${activePlayer}/tee`
        : `/rounds/${round.id}/tee`;
      const { data } = await api.patch(url, { tee_color: next || null });
      // Only remember non-fourball solo tees on the device — fourball picks
      // are per-player and per-round, so we don't bias the next solo round.
      if (!isFourball) {
        try { localStorage.setItem("fairwayTeeColor", next || ""); } catch { /* noop */ }
      }
      onTeeChanged?.(data);
      setOpen(false);
    } catch {
      // silent — pill stays on previous tee
    } finally {
      setBusy(false);
    }
  };

  // Show the active player's first name in fourball mode so it's obvious
  // whose tee you're toggling.
  const firstName = isFourball ? (player?.name || "").split(" ")[0] : "";

  return (
    <div className="relative" data-testid="round-tee-switch">
      <button
        type="button"
        disabled={busy}
        onClick={() => setOpen((v) => !v)}
        data-testid="round-tee-switch-pill"
        className="inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[10px] font-bold font-heading uppercase tracking-wider whitespace-nowrap bg-white/10 text-white hover:bg-white/15 transition-colors disabled:opacity-60"
      >
        <span
          className="h-2.5 w-2.5 rounded-full border border-white/30 shrink-0"
          style={{ background: currentOpt.swatch === "transparent" ? "rgba(255,255,255,0.15)" : currentOpt.swatch }}
        />
        {isFourball && firstName ? `${firstName}: ${currentOpt.label}` : currentOpt.label}
        <span className="opacity-60">▾</span>
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div
            className="absolute right-0 mt-1 z-50 min-w-[160px] rounded-xl bg-white shadow-xl border border-black/10 p-1"
            data-testid="round-tee-switch-menu"
          >
            {isFourball && (
              <div className="px-2.5 pt-1.5 pb-1 text-[10px] font-bold uppercase tracking-wider text-gray-500">
                {firstName || `Player ${activePlayer + 1}`}&apos;s tee
              </div>
            )}
            {showOpts.map((opt) => (
              <button
                key={opt.id || "default"}
                type="button"
                disabled={busy}
                onClick={() => pick(opt.id)}
                data-testid={`round-tee-switch-${opt.id || "default"}`}
                className={`w-full flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-xs font-bold normal-case tracking-normal transition-colors ${
                  opt.id === current
                    ? "bg-[var(--primary)]/10 text-[var(--primary)]"
                    : "hover:bg-[var(--accent-sand)] text-gray-900"
                }`}
              >
                <span
                  className="h-3 w-3 rounded-full border border-black/20 shrink-0"
                  style={{ background: opt.swatch === "transparent" ? "#e5e5e5" : opt.swatch }}
                />
                {opt.label}
                {opt.id === current && <span className="ml-auto text-[10px] opacity-70">✓</span>}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
